import type { ReactNode } from "react";
import { IndicatorCard, ProgressBar } from "../../components/index.js";
import type { DashboardSummary } from "../../types.js";

/**
 * Card 2 — Total enviado (Design.md §5.2). Todos os números são REAIS
 * (GET /api/v1/dashboard): número grande = totalShippedUnits; "Por fornecedor" =
 * bySupplier; "Por SKU" = bySku. Só contam planos com etiquetas de caixa prontas
 * (BOX_LABELS_READY) — ver services/dashboard-aggregation.ts. Enquanto não houver
 * envio concluído, as colunas mostram o estado vazio honesto.
 */
export interface TotalShippedCardProps {
  dashboard: DashboardSummary | null;
}

export function TotalShippedCard({ dashboard }: TotalShippedCardProps): ReactNode {
  const total = dashboard !== null ? dashboard.totalShippedUnits : 0;
  const bySupplier = dashboard?.bySupplier ?? [];
  const bySku = dashboard?.bySku ?? [];
  const maxSupplierUnits = bySupplier.reduce(
    (max, s) => (s.units > max ? s.units : max),
    0
  );

  return (
    <IndicatorCard
      title="Total enviado"
      value={dashboard !== null ? total.toLocaleString("pt-BR") : "—"}
      detail="unidades (todos os fornecedores)"
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "1.25rem"
        }}
      >
        <div>
          <div style={columnTitleStyle}>Por fornecedor</div>
          {bySupplier.length === 0 ? (
            <p style={emptyStyle}>Nenhum envio concluído ainda.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {bySupplier.map((s) => (
                <ProgressBar
                  key={s.supplierId}
                  value={s.units}
                  max={maxSupplierUnits}
                  tone="blue"
                  label={s.supplierName}
                  trailing={s.units.toLocaleString("pt-BR")}
                />
              ))}
            </div>
          )}
        </div>

        <div>
          <div style={columnTitleStyle}>Por SKU</div>
          {bySku.length === 0 ? (
            <p style={emptyStyle}>Nenhum envio concluído ainda.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
              {bySku.map((row) => (
                <div
                  key={row.msku}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: 12
                  }}
                >
                  <span className="mono" style={{ color: "var(--text-muted)" }}>
                    {row.msku}
                  </span>
                  <span>{row.units.toLocaleString("pt-BR")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </IndicatorCard>
  );
}

const columnTitleStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--text-muted)",
  marginBottom: "0.5rem",
  display: "flex",
  alignItems: "center",
  gap: "0.4rem"
} as const;

const emptyStyle = { fontSize: 11.5, color: "var(--text-muted)" } as const;
